import React, { useEffect, useState } from 'react';
import { Star, Plus, RefreshCw, Save, Trash2, Sparkles, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../../../lib/adminApi';

/**
 * Admin editor for the 5-Star review categories (Clinical Excellence, etc).
 * Embedded inside the Templates tab in AdminHub — sits below the TAH section.
 */
export const FiveStarCategoriesSection = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [savingId, setSavingId] = useState('');
  const [draft, setDraft] = useState({ name: '', description: '' });
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminApi.categoriesList();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message || 'Failed to load 5-Star categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const seedDefaults = async () => {
    setSeeding(true);
    try {
      const r = await adminApi.categoriesSeedDefaults();
      if (r.seeded) toast.success(`Seeded ${r.count} default categories`);
      else toast.info(r.message || 'Already seeded');
      load();
    } catch (err) {
      toast.error(err.message || 'Seed failed');
    } finally {
      setSeeding(false);
    }
  };

  const create = async (e) => {
    e?.preventDefault?.();
    if (!draft.name.trim()) return;
    setCreating(true);
    try {
      await adminApi.categoryCreate({
        name: draft.name.trim(),
        description: draft.description.trim(),
        order: items.length,
      });
      setDraft({ name: '', description: '' });
      toast.success('Category added');
      load();
    } catch (err) {
      toast.error(err.message || 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  const saveRow = async (item, patch) => {
    setSavingId(item.id);
    try {
      await adminApi.categoryUpdate(item.id, patch);
      load();
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSavingId('');
    }
  };

  const toggleActive = (item) => saveRow(item, { is_active: !item.is_active });

  const remove = async (item) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Deactivate "${item.name}"? Past reviews keep their data; new reviews will no longer include this category.`)) return;
    try {
      await adminApi.categoryDelete(item.id);
      toast.success('Deactivated');
      load();
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    }
  };

  return (
    <div data-testid="five-star-categories-section" className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
            <Star className="w-4 h-4 text-fuchsia-600" /> 5-Star Review Categories
          </div>
          <div className="text-xs text-slate-500">
            Categories shown on every 5-Star review rating step. Edit name / description, deactivate to hide from new reviews.
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border border-slate-300 hover:bg-slate-50">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          {items.length === 0 && (
            <button
              onClick={seedDefaults}
              disabled={seeding}
              data-testid="five-star-seed-defaults"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-fuchsia-600 text-white hover:bg-fuchsia-700 disabled:opacity-50"
            >
              <Sparkles className="w-3.5 h-3.5" /> {seeding ? 'Seeding…' : 'Seed Defaults'}
            </button>
          )}
        </div>
      </div>

      {!loading && items.length === 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-xs text-amber-900">
          <strong>No categories defined.</strong> Click <em>Seed Defaults</em> above to insert the 5 standard categories
          (Clinical Excellence, Team Collaboration, Productivity & Efficiency, Professional Growth,
          Patient & Family Engagement). You can edit them afterwards.
        </div>
      )}

      <div className="space-y-2">
        {loading && <div className="text-sm text-slate-500 py-4">Loading…</div>}
        {!loading && items.map((item) => (
          <CategoryRow
            key={item.id}
            item={item}
            isSaving={savingId === item.id}
            onSave={(patch) => saveRow(item, patch)}
            onToggle={() => toggleActive(item)}
            onRemove={() => remove(item)}
          />
        ))}
      </div>

      {!loading && (
        <form onSubmit={create} className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 space-y-2">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Add a new category</div>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2">
            <input
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="Name (e.g. Documentation Quality)"
              data-testid="five-star-cat-new-name"
              className="px-3 py-2 text-sm rounded-md border border-slate-300 focus:outline-none focus:border-fuchsia-400 bg-white"
            />
            <input
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              placeholder="Short description shown under the title"
              className="px-3 py-2 text-sm rounded-md border border-slate-300 focus:outline-none focus:border-fuchsia-400 bg-white"
            />
            <button type="submit" disabled={creating || !draft.name.trim()} data-testid="five-star-cat-add-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold bg-fuchsia-600 text-white hover:bg-fuchsia-700 disabled:opacity-50">
              <Plus className="w-3.5 h-3.5" /> {creating ? 'Adding…' : 'Add'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

const CategoryRow = ({ item, isSaving, onSave, onToggle, onRemove }) => {
  const [name, setName] = useState(item.name);
  const [description, setDescription] = useState(item.description || '');
  const dirty = name !== item.name || description !== (item.description || '');

  return (
    <div
      className={`rounded-xl border bg-white p-3 ${item.is_active ? 'border-slate-200' : 'border-slate-200 opacity-60'}`}
      data-testid={`five-star-cat-${item.id}`}
    >
      <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2 items-start">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="px-3 py-2 text-sm font-semibold rounded-md border border-slate-200 focus:outline-none focus:border-fuchsia-400 bg-white"
        />
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          className="px-3 py-2 text-sm rounded-md border border-slate-200 focus:outline-none focus:border-fuchsia-400 bg-white resize-y"
        />
        <div className="flex flex-col items-end gap-1.5">
          {dirty && (
            <button
              onClick={() => onSave({ name: name.trim(), description: description.trim() })}
              disabled={isSaving || !name.trim()}
              data-testid={`five-star-cat-save-${item.id}`}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded bg-fuchsia-600 text-white hover:bg-fuchsia-700 disabled:opacity-50"
            >
              <Save className="w-3 h-3" /> {isSaving ? 'Saving…' : 'Save'}
            </button>
          )}
          <div className="flex items-center gap-0.5">
            <button onClick={onToggle} title={item.is_active ? 'Deactivate' : 'Activate'} className="p-1.5 rounded hover:bg-slate-100">
              {item.is_active
                ? <ToggleRight className="w-5 h-5 text-emerald-600" />
                : <ToggleLeft className="w-5 h-5 text-slate-400" />}
            </button>
            <button onClick={onRemove} title="Deactivate" className="text-slate-400 hover:text-rose-600 p-1.5">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
